import i18n from "../language/i18n";

export const ONE_HOUR = 3600000;
export const ONE_DAY = 86400000;
export const ONE_WEEK = 604800000;
export const ONE_MONTH = 2419200000;
export const THREE_MONTHS = 7257600000;

export const retrieveMonthNameByIndex = (index: number) => {
  if (index === 0) {
    return i18n.t("january");
  } else if (index === 1) {
    return i18n.t("february");
  } else if (index === 2) {
    return i18n.t("march");
  } else if (index === 3) {
    return i18n.t("april");
  } else if (index === 4) {
    return i18n.t("may");
  } else if (index === 5) {
    return i18n.t("june");
  } else if (index === 6) {
    return i18n.t("july");
  } else if (index === 7) {
    return i18n.t("august");
  } else if (index === 8) {
    return i18n.t("september");
  } else if (index === 9) {
    return i18n.t("october");
  } else if (index === 10) {
    return i18n.t("november");
  } else if (index === 11) {
    return i18n.t("december");
  }
};

export const retrieveDateIntervalOptionList = () => {
  return [
    {
      value: "LAST_DAY",
      text: i18n.t("last_day"),
    },
    {
      value: "LAST_WEEK",
      text: i18n.t("last_week"),
    },
    {
      value: "LAST_MONTH",
      text: i18n.t("last_month"),
    },
    {
      value: "LAST_THREE_MONTHS",
      text: i18n.t("last_three_months"),
    },
  ];
};

export const calculateStartDateOfWeek = (date: Date, isGMT: boolean) => {
  const dateCopy = new Date(date);

  if (isGMT) {
    const diff =
      dateCopy.getUTCDate() -
      dateCopy.getUTCDay() +
      (dateCopy.getUTCDay() === 0 ? -6 : 1);
    dateCopy.setUTCDate(diff);
    dateCopy.setUTCHours(0, 0, 0, 0);
    return dateCopy;
  } else {
    const diff =
      dateCopy.getDate() -
      dateCopy.getDay() +
      (dateCopy.getDay() === 0 ? -6 : 1);
    dateCopy.setDate(diff);
    dateCopy.setHours(0, 0, 0, 0);
    return dateCopy;
  }
};

export const calculateEndDateOfWeek = (date: Date, isGMT: boolean) => {
  const endDate = new Date(date);

  if (isGMT) {
    const dayOfWeek = endDate.getUTCDay();
    const daysUntilSunday = (7 - dayOfWeek) % 7;

    endDate.setUTCDate(endDate.getUTCDate() + daysUntilSunday);
    endDate.setUTCHours(23, 59, 59, 999);
    return endDate;
  } else {
    const dayOfWeek = endDate.getDay();
    const daysUntilSunday = (7 - dayOfWeek) % 7;

    endDate.setDate(endDate.getDate() + daysUntilSunday);
    endDate.setHours(23, 59, 59, 999);
    return endDate;
  }
};

export const constructMonthName = (date: Date): string => {
  return `${retrieveMonthNameByIndex(date.getMonth())} ${date.getFullYear()}`;
};

export const constructWeekName = (
  beginDate: Date,
  endDate: Date,
  untilSunday: boolean
) => {
  let currentEndDate;
  if (endDate) {
    currentEndDate = endDate;
  } else {
    let offset = 4;
    if (untilSunday) {
      offset = 6;
    }
    currentEndDate = new Date(beginDate);
    currentEndDate.setDate(beginDate.getDate() + offset);
  }
  if (beginDate.getFullYear() !== currentEndDate.getFullYear()) {
    return `${beginDate.getDate()} ${retrieveMonthNameByIndex(
      beginDate.getMonth()
    )} ${beginDate.getFullYear()} - ${currentEndDate.getDate()} ${retrieveMonthNameByIndex(
      currentEndDate.getMonth()
    )} ${currentEndDate.getFullYear()}`;
  } else {
    if (beginDate.getMonth() !== currentEndDate.getMonth()) {
      return `${beginDate.getDate()} ${retrieveMonthNameByIndex(
        beginDate.getMonth()
      )} - ${currentEndDate.getDate()} ${retrieveMonthNameByIndex(
        currentEndDate.getMonth()
      )} ${currentEndDate.getFullYear()}`;
    } else {
      return `${beginDate.getDate()} - ${currentEndDate.getDate()} ${retrieveMonthNameByIndex(
        currentEndDate.getMonth()
      )} ${currentEndDate.getFullYear()}`;
    }
  }
};

/**
 * Whether a timestamp falls inside one of `retrieveTimeFilterOptionList`'s windows.
 *
 * The report screen's window logic previously existed only INSIDE the outcome graphs, applied per
 * tree node. Anything else on that tab that needs the same window — a chart, a list — needs it as a
 * plain scalar test, which is what this is. (The graphs keep their own copy for now; unifying them
 * is a separate change with its own regression surface.)
 *
 * `"all"` (and any unrecognized value) matches everything; a missing or invalid timestamp never
 * matches a bounded window. Bounds are browser-local, which is close enough for a coarse report
 * filter and matches how the graphs already compute theirs.
 */
export const isWithinTimeFilterWindow = (
  timestamp: number | null | undefined,
  timeFilterWithN: string | null | undefined
): boolean => {
  if (!timeFilterWithN || timeFilterWithN === "all") return true;
  if (timestamp == null) return false;

  const scoreDate = new Date(timestamp);
  if (isNaN(scoreDate.getTime())) return false;

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastNDaysStart = (n: number) => {
    const d = new Date(today);
    d.setDate(today.getDate() - n);
    return d;
  };

  switch (timeFilterWithN) {
    case "today":
      return scoreDate >= today;
    case "yesterday":
      return scoreDate >= lastNDaysStart(1) && scoreDate < today;
    case "this_month":
      return scoreDate >= thisMonthStart;
    case "last_month":
      // Exclusive upper bound = start of this month, so the whole last calendar day of the previous
      // month stays in window (a `<= new Date(y, m, 0)` bound would be that day at 00:00 and would
      // silently drop anything recorded later that day).
      return scoreDate >= lastMonthStart && scoreDate < thisMonthStart;
    case "last_7_days":
      return scoreDate >= lastNDaysStart(7);
    case "last_30_days":
      return scoreDate >= lastNDaysStart(30);
    case "last_90_days":
      return scoreDate >= lastNDaysStart(90);
    default:
      return true;
  }
};

export const retrieveTimeFilterOptionList = (isDetailed: boolean) => {
  const baseTimeFilterOptionList = [
    {
      text: i18n.t("all_time"),
      title: i18n.t("all_time"),
      value: "all",
      id: "all",
    },
  ];
  if (isDetailed) {
    baseTimeFilterOptionList.push(
      {
        text: i18n.t("today"),
        title: i18n.t("today"),
        value: "today",
        id: "today",
      },
      {
        text: i18n.t("yesterday"),
        title: i18n.t("yesterday"),
        value: "yesterday",
        id: "yesterday",
      },
      {
        text: i18n.t("this_week"),
        title: i18n.t("this_week"),
        value: "this_week",
        id: "this_week",
      },
      {
        text: i18n.t("last_week"),
        title: i18n.t("last_week"),
        value: "last_week",
        id: "last_week",
      }
    );
  }

  baseTimeFilterOptionList.push(
    {
      text: i18n.t("this_month"),
      title: i18n.t("this_month"),
      value: "this_month",
      id: "this_month",
    },
    {
      text: i18n.t("last_month"),
      title: i18n.t("last_month"),
      value: "last_month",
      id: "last_month",
    }
  );

  if (isDetailed) {
    baseTimeFilterOptionList.push({
      text: i18n.t("last_7_days"),
      title: i18n.t("last_7_days"),
      value: "last_7_days",
      id: "last_7_days",
    });
  }

  baseTimeFilterOptionList.push(
    {
      text: i18n.t("last_30_days"),
      title: i18n.t("last_30_days"),
      value: "last_30_days",
      id: "last_30_days",
    },
    {
      text: i18n.t("last_90_days"),
      title: i18n.t("last_90_days"),
      value: "last_90_days",
      id: "last_90_days",
    }
  );
  return baseTimeFilterOptionList;
};

export const retrieveLastNOptionList = () => {
  const baseTimeFilterOptionList = [
    {
      text: i18n.t("last_2"),
      title: i18n.t("last_2"),
      value: "last_2",
      id: "last_2",
    },
    {
      text: i18n.t("last_3"),
      title: i18n.t("last_3"),
      value: "last_3",
      id: "last_3",
    },
    {
      text: i18n.t("last_4"),
      title: i18n.t("last_4"),
      value: "last_4",
      id: "last_4",
    },
    {
      text: i18n.t("last_5"),
      title: i18n.t("last_5"),
      value: "last_5",
      id: "last_5",
    },
    {
      text: i18n.t("last_10"),
      title: i18n.t("last_10"),
      value: "last_10",
      id: "last_10",
    },
  ];
  return baseTimeFilterOptionList;
};

export const retrieveTimeFilterWithLastNOptionList = (isDetailed: boolean) => {
  const timeFilterOptionList = retrieveTimeFilterOptionList(isDetailed);
  const lastNOptionList = retrieveLastNOptionList();
  return [
    timeFilterOptionList[0],
    ...lastNOptionList,
    ...timeFilterOptionList.slice(1),
  ];
};

export const getOneWeekLater = (dateString: string) => {
  const date = new Date(dateString);
  date.setDate(date.getDate() + 7);
  const localDate = new Date(
    date.getTime() - date.getTimezoneOffset() * 60 * 1000
  );
  return localDate.toISOString().slice(0, 16);
};

export const getCurrentDateTime = () => {
  return new Date(
    new Date().getTime() - new Date().getTimezoneOffset() * 60 * 1000
  )
    .toISOString()
    .slice(0, 16);
};

// Human duration label for a millisecond amount — e.g. "1 saat 39 dk", "2 saat", "45 dk".
// Rounds down to whole minutes; under a minute reads "0 dk" so a fresh counter is still legible.
export const retrieveDurationLabel = (millis: number): string => {
  const totalMinutes = Math.max(0, Math.floor((millis || 0) / 60000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours} ${i18n.t("hour_abbrevation")}`);
  if (minutes > 0 || hours === 0) parts.push(`${minutes} ${i18n.t("minute_abbrevation")}`);
  return parts.join(" ");
};
